import { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ErrorAlert, InfoAlert, SuccessAlert } from "@shared/ui/alerts";
import { InputController } from "@shared/ui/form";
import { validateTimesheetWeekByProject } from "../api/timeEntryApi";
import { useProjectIssues } from "../hooks/useProjectIssues";
import { useChargeHours } from "../hooks/useChargeHours";
import { useStoredProjectAssignments } from "../hooks/useStoredProjectAssignments";
import { buildRegisterHourDescriptiveSchema } from "../schema/registerHourDescriptiveSchema";
import { buildDescriptiveEntry } from "../lib/timeEntryMappers";
import { sumHours } from "../lib/hourRules";
import { hasNoProjectOptions } from "../lib/projectAssignments";
import { SelectProjectRegisterHour } from "./SelectProjectRegisterHour";
import { UnmatchedAssignmentsAlert } from "./UnmatchedAssignmentsAlert";

import type { RegisterHourDescriptiveValues } from "../schema/registerHourDescriptiveSchema";
import type { ChargeHoursPayload } from "../api/timeEntryApi";

import type { TFunction } from "i18next";

export interface RegisterHourDescriptiveProps {
  handleClose: () => void;
  open: boolean;
  notLoading: () => void;
  loading: () => void;
  reloadInitialMissingHour: () => void;
  t: TFunction;
}

export const RegisterHourDescriptive = ({
  handleClose,
  open,
  notLoading,
  loading,
  reloadInitialMissingHour,
  t,
}: RegisterHourDescriptiveProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<RegisterHourDescriptiveValues>({
    resolver: yupResolver(buildRegisterHourDescriptiveSchema(t)) as never,
    defaultValues: { total: 0 },
    mode: "onChange",
  });

  const projectId = watch("projectId");
  const total = watch("total");
  const hourProductive = watch("hourProductive");
  const hourPaidLeave = watch("hourPaidLeave");
  const hourDisease = watch("hourDisease");
  const hourUnpaidLeave = watch("hourUnpaidLeave");

  const {
    all: listProject,
    staffing,
    projects,
    unmatched,
    supportReference,
  } = useStoredProjectAssignments(open);

  const { data: projectIssues } = useProjectIssues(projectId);
  const issueId = projectIssues?.[0]?.id;

  // Total = suma de todas las categorías de horas.
  useEffect(() => {
    setValue("total", sumHours(hourProductive, hourPaidLeave, hourUnpaidLeave, hourDisease));
  }, [hourProductive, hourPaidLeave, hourUnpaidLeave, hourDisease]);

  const onClose = () => {
    handleClose();
    reset();
  };

  // Avisa si la semana ya fue imputada al seleccionar un proyecto.
  // `projectId` es `string` una vez casteado por yup al enviar, pero el valor crudo que
  // devuelve `watch()` acá es el id numérico del <MenuItem> (ver useProjectIssues.ts para
  // el mismo caso). `Number(...)` documenta ese valor real sin cambiar el resultado.
  useEffect(() => {
    if (Number(projectId) > 0) {
      validateTimesheetWeekByProject(projectId).then((data) => {
        if (data.isChargedHourInWeek) {
          onClose();
          InfoAlert(t("informationTitle"), t("hourChargedMessage"));
        }
      });
    }
  }, [projectId]);

  const chargeHours = useChargeHours();

  const onRegister = (values: RegisterHourDescriptiveValues) => {
    onClose();
    loading();
    // `issueId` puede ser undefined si el proyecto no tiene issues (comportamiento
    // preexistente sin guard, ver #2572); se documenta con `!` en vez de forzar un
    // guard nuevo que cambiaría el comportamiento.
    const entry = buildDescriptiveEntry(values, { issueId: issueId! });

    // `buildDescriptiveEntry` tipa su retorno con los campos de
    // `Partial<RegisterHourDescriptiveValues>` (opcionales) aunque en runtime `values`
    // siempre viene completo desde el form validado por yup; se documenta el cast en vez
    // de aflojar el tipo del builder.
    chargeHours.mutate(entry as ChargeHoursPayload, {
      onSuccess: () => {
        notLoading();
        SuccessAlert(t("loadHours"), t("doneSuccessfully", { a: "a" })).then(() =>
          reloadInitialMissingHour()
        );
      },
      onError: (error) => {
        notLoading();
        onClose();
        ErrorAlert(t("errorTitle"), error.response?.data?.message ?? t("internalError")).then();
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onRegister)}>
        <DialogTitle>{t("descriptiveHourLoad")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid size={12}>
              <UnmatchedAssignmentsAlert unmatched={unmatched} supportReference={supportReference} t={t} />
              <FormControl fullWidth sx={{ mt: 1 }}>
                <SelectProjectRegisterHour
                  control={control}
                  defaultValue={[]}
                  label={t("project")}
                  name="projectId"
                  listValue={listProject}
                  listStaffing={staffing}
                  listProjects={projects}
                  disabled={hasNoProjectOptions(staffing, projects)}
                />
                {errors.projectId && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.projectId.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputController
                  control={control}
                  defaultValue={0}
                  label={t("hourProductive")}
                  name="hourProductive"
                />
                {errors.hourProductive && (
                  <span style={{ fontSize: 12, color: "red" }}>
                    {errors.hourProductive.message}
                  </span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputController
                  control={control}
                  defaultValue={0}
                  label={t("hourPaidLeave")}
                  name="hourPaidLeave"
                />
                {errors.hourPaidLeave && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.hourPaidLeave.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputController
                  control={control}
                  defaultValue={0}
                  label={t("hourDisease")}
                  name="hourDisease"
                />
                {errors.hourDisease && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.hourDisease.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputController
                  control={control}
                  defaultValue={0}
                  label={t("hourUnpaidLeave")}
                  name="hourUnpaidLeave"
                />
                {errors.hourUnpaidLeave && (
                  <span style={{ fontSize: 12, color: "red" }}>
                    {errors.hourUnpaidLeave.message}
                  </span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <Box
                sx={{ display: "flex", flexDirection: "column", justifyContent: "end" }}
                style={{ marginLeft: 2, marginRight: 20 }}
              >
                <Typography variant="h6">
                  {t("totalHours")}: {total}
                </Typography>
                {errors.total && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.total.message}</span>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("cancel")}</Button>
          <Button type="submit">{t("load")}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
